import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import SEO from "@/components/SEO";
import TestimonialsStrip from "@/components/TestimonialsStrip";
import { ArrowRight, MagnifyingGlass, Buildings, MapTrifold, ChartLineUp, Handshake, Key, ArrowUpRight, ShieldCheck, WhatsappLogo, Article } from "@phosphor-icons/react";
import { useLang } from "@/lib/lang";
import { faqSchema, itemListSchema } from "@/lib/schema";

// Homepage-level FAQs — the widest funnel for AEO. These answers should
// stand alone and read like authoritative Point Penta soundbites (they will
// be quoted verbatim by Google AI Overviews, Perplexity, ChatGPT search).
const HOME_FAQS = [
  { q: "Is now a good time to buy Dubai property in 2026?", a: "Point Penta's data desk tracks Dubai transactions running roughly 20% above 2024 volumes, with prime areas (Downtown, Marina, Palm Jumeirah) still showing single-digit annual appreciation. Yields of 5.4–7.8% keep Dubai one of the world's most attractive markets for both end-users and investors, especially given zero personal income tax and Golden Visa residency at AED 2M+." },
  { q: "Do I need a UAE residency visa to buy property in Dubai?", a: "No. Foreign buyers can purchase freehold in designated areas without residency. Purchase itself unlocks a 2-year investor visa (AED 750K+) or a 10-year Golden Visa (AED 2M+), so most international clients buy first and receive residency as a consequence." },
  { q: "What is the average rental yield in Dubai?", a: "Point Penta's live community metrics engine currently reports gross yields ranging from 5.4% (Palm Jumeirah — prestige premium) to 7.8% (JVC — mid-market). Downtown Dubai averages 6.1%, Dubai Marina 6.8%, Business Bay 7.1%. Every community page shows a live yield refreshed from active mandates daily." },
  { q: "How much does it cost to buy property in Dubai?", a: "Budget approximately 7% on top of the property price: 4% Dubai Land Department transfer fee, 2% agent commission, ~1% miscellaneous (NOC, registration, oqood, title deed). A cash purchase closes in 7–14 days; mortgage-backed purchases settle in 30–45 days." },
  { q: "Can I get a mortgage in Dubai as a non-resident?", a: "Yes. UAE banks lend to non-residents at up to 50–60% loan-to-value, with rates in 2026 ranging 4.5–6.5% (variable) and 5.0–6.5% (fixed). Point Penta's mortgage desk handles pre-qualification with all major banks (Emirates NBD, Mashreq, HSBC, ADCB, Standard Chartered) in 24–72 hours." },
  { q: "What is the difference between off-plan and ready property in Dubai?", a: "Off-plan is bought directly from the developer before completion, with payments staggered across construction milestones — attractive for price appreciation and modern spec. Ready property gives instant possession, rental income and mortgage eligibility, but at a market-set premium. Point Penta advises on both sides depending on your holding horizon." },
  { q: "Which developers are most trusted in Dubai?", a: "Tier-1 masters are Emaar Properties (Downtown), Nakheel (Palm Jumeirah), DAMAC Properties (luxury villas), Sobha Realty (Sobha Hartland), Meraas (City Walk / Bluewaters), Aldar Properties (Abu Dhabi), Select Group (Marina), Ellington Properties (boutique) and Dubai Properties (Business Bay). Point Penta partners directly with each." },
  { q: "How does Point Penta select properties?", a: "Every mandate that appears on Point Penta is vetted by a RERA-licensed advisor for title clarity, service-charge history, floor plan quality and comparable pricing. We deliver a curated shortlist within 24 hours of your brief — not a flood of listings." },
  { q: "Do you handle rentals only from private landlords?", a: "Yes. Our rental desk works exclusively with landlords who prefer curated tenant introductions to open marketing. Tenants receive matched shortlists (typically 3–5 mandates) within 24 hours of their brief." },
  { q: "In how many languages does Point Penta operate?", a: "Point Penta serves clients in seven languages — English, Arabic, Russian, Chinese (Mandarin), French, Japanese and Korean. Every property page, community guide and journal article is available in all seven." },
];

const HERO_IMG = "https://images.unsplash.com/photo-1546412414-8035e1776c9a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxkdWJhaSUyMGx1eHVyeSUyMG1vZGVybiUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODMzMzMzMzN8MA&ixlib=rb-4.1.0&q=85";

// Ken-Burns slideshow images for the hero column. Each frame slowly zooms
// (like the macOS aerial screensavers) and cross-fades to the next after ~11s.
// Sourced by the client; downloaded from their Google Drive and hosted on
// our own object storage so we don't depend on Google's uptime / policies.
const HERO_KENBURNS = [
  "/api/files/hero/hero-1-1thVk1BJ.jpg",
  "/api/files/hero/hero-2-1yQOas14.jpg",
  "/api/files/hero/hero-3-1ckhFVBn.jpg",
  "/api/files/hero/hero-4-1bZHuwdt.jpg",
].map((p) => `${process.env.REACT_APP_BACKEND_URL}${p}`);

// Count-up hook — retained for possible reuse elsewhere.
function useCountUp(target, opts = {}) {
  const { duration = 1400, decimals = 0 } = opts;
  const [value, setValue] = useState(0);
  const nodeRef = useRef(null);
  useEffect(() => {
    if (!nodeRef.current || !target) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        const start = performance.now();
        const raf = (now) => {
          const p = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - p, 3);
          setValue(Number((target * eased).toFixed(decimals)));
          if (p < 1) requestAnimationFrame(raf);
        };
        requestAnimationFrame(raf);
        io.disconnect();
      }
    }, { threshold: 0.5 });
    io.observe(nodeRef.current);
    return () => io.disconnect();
  }, [target, duration, decimals]);
  return [value, nodeRef];
}

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [areas, setAreas] = useState([]);
  const [topInvest, setTopInvest] = useState([]);
  const [latestJournal, setLatestJournal] = useState(null);
  const [q, setQ] = useState("");
  const [slide, setSlide] = useState(0);
  const { t } = useLang();

  useEffect(() => {
    (async () => {
      try {
        const [f, fp, ac, ti, jr] = await Promise.all([
          api.get("/listings", { params: { featured: true, limit: 6 } }),
          api.get("/projects", { params: { featured: true } }),
          api.get("/communities", { params: { featured: true, limit: 5 } }),
          api.get("/communities", { params: { sort: "investment_score", limit: 5 } }),
          api.get("/journal"),
        ]);
        setFeatured(f.data.items);
        setFeaturedProjects((fp.data.items || []).slice(0, 3));
        let communities = ac.data.items || [];
        if (communities.length === 0) {
          const a = await api.get("/areas");
          communities = a.data.items.slice(0, 5);
        }
        setAreas(communities);
        setTopInvest((ti.data.items || []).slice(0, 5));
        setLatestJournal((jr.data.items || [])[0] || null);
      } catch { /* no-op */ }
    })();
  }, []);

  // Ken-Burns slideshow: advance every 11s. Each layer independently animates
  // via CSS keyframes so pausing the tab pauses the animation naturally.
  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_KENBURNS.length), 11000);
    return () => clearInterval(t);
  }, []);

  return (
    <div data-testid="home-page">
      <SEO
        title="Boutique Dubai brokerage"
        description="Point Penta Real Estate — curated Dubai residential, off-plan and commercial mandates for private owners, investors and international families."
        path="/"
        schema={[
          faqSchema(HOME_FAQS),
          topInvest.length > 0 && itemListSchema({
            name: "Top investment communities in Dubai — 2026",
            items: topInvest.map((c) => ({ name: c.name, path: `/areas/${c.slug}` })),
          }),
        ].filter(Boolean)}
      />

      {/* HERO — full-bleed background image with slow Ken-Burns zoom (like the
          macOS Aerial screensaver). Image extends behind the transparent nav
          and fills the full viewport. Copy is anchored inside the safe area
          via top padding equal to the nav height (80px). */}
      <section
        className="relative bg-black text-white overflow-hidden"
        style={{ minHeight: "100svh" }}
        data-testid="home-hero"
      >
        {/* Full-bleed Ken Burns stack — each frame independently zooms + pans,
            layers cross-fade every 11s. Kept in DOM so images stay cache-warm. */}
        {HERO_KENBURNS.map((src, i) => (
          <img
            key={src}
            src={src}
            alt=""
            loading={i === 0 ? "eager" : "lazy"}
            className={`pp-kenburns absolute inset-0 w-full h-full object-cover transition-opacity duration-[1400ms] ease-out ${i === slide ? "opacity-100" : "opacity-0"}`}
            aria-hidden={i !== slide}
          />
        ))}

        {/* Readability scrims — dark bottom-to-top for the copy stack, plus a
            left-side vignette so the headline never fights a bright sky, plus
            a top scrim so the transparent nav stays legible. */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/20 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/15 to-transparent pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/45 to-transparent pointer-events-none" />

        {/* Hero copy + action bar — pt-32 clears the fixed 80px nav */}
        <div className="relative z-10 flex flex-col justify-between pt-32 lg:pt-40 pb-6 lg:pb-8" style={{ minHeight: "100svh" }}>
          <div className="flex-1 max-w-[1440px] mx-auto px-6 lg:px-12 w-full flex items-center">
            <div className="max-w-3xl pp-reveal">
              <div className="overline text-white/85 mb-5 lg:mb-7" data-testid="hero-kicker">Point Penta · Dubai · Est. 2026</div>
              <h1
                className="font-serif text-4xl sm:text-6xl lg:text-[92px] xl:text-[104px] leading-[0.95] tracking-tight text-white mb-6 lg:mb-8 break-words"
                style={{ textShadow: "0 2px 24px rgba(0,0,0,0.4)" }}
                data-testid="hero-headline"
              >
                {t("home.hero1")}<br />
                <span>{t("home.hero2")}</span>
              </h1>
              <p className="text-white/90 text-base sm:text-lg max-w-xl leading-relaxed mb-6 lg:mb-8" style={{ textShadow: "0 1px 12px rgba(0,0,0,0.45)" }}>
                {t("home.intro")}
              </p>

              <form
                onSubmit={(e) => { e.preventDefault(); window.location.href = `/properties?q=${encodeURIComponent(q)}`; }}
                className="flex items-stretch bg-white/95 backdrop-blur-sm w-full max-w-2xl shadow-2xl"
                data-testid="hero-search"
              >
                <div className="pl-4 sm:pl-5 flex items-center text-neutral-500"><MagnifyingGlass size={20} /></div>
                <input
                  value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder={t("home.searchPlaceholder")}
                  className="flex-1 min-w-0 bg-transparent px-3 sm:px-4 py-3 sm:py-4 text-sm sm:text-base text-[#050d19] placeholder:text-neutral-500 focus:outline-none"
                  data-testid="hero-search-input"
                />
                <button className="px-4 sm:px-8 bg-[#001588] text-white text-sm sm:text-base tracking-wide hover:bg-black transition-colors" data-testid="hero-search-btn">{t("cta.search")}</button>
              </form>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-4 lg:mt-6 text-sm text-white/85">
                <Link to="/properties" className="hover:text-white transition-colors">{t("nav.buy")}</Link>
                <span className="text-white/30">/</span>
                <Link to="/rent" className="hover:text-white transition-colors">{t("nav.rent")}</Link>
                <span className="text-white/30">/</span>
                <Link to="/projects" className="hover:text-white transition-colors">{t("nav.projects")}</Link>
                <span className="text-white/30">/</span>
                <Link to="/commercial" className="hover:text-white transition-colors">Commercial</Link>
              </div>
            </div>
          </div>

          {/* Slide indicators — animated tick bars anchored bottom-right of the frame */}
          <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12 w-full flex items-center justify-end mb-4 lg:mb-6">
            <div className="flex items-center gap-2">
              {HERO_KENBURNS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setSlide(i)}
                  aria-label={`Show frame ${i + 1}`}
                  data-testid={`hero-dot-${i}`}
                  className={`h-[3px] transition-all duration-500 ${i === slide ? "w-10 bg-white" : "w-5 bg-white/45 hover:bg-white/80"}`}
                  style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.35)" }}
                />
              ))}
            </div>
          </div>

          {/* Action bar — glass strip anchored at the bottom of the hero */}
          <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12 w-full">
            <div className="grid grid-cols-1 md:grid-cols-3 border-t border-white/25 divide-y md:divide-y-0 md:divide-x divide-white/25 bg-black/35 backdrop-blur-md">
              {/* 1 — Free valuation */}
              <Link to="/valuation" data-testid="hero-action-valuation"
                    className="group flex items-start gap-4 py-6 lg:py-7 px-6 lg:px-8 hover:bg-white/10 transition-colors">
                <ShieldCheck size={26} className="text-white mt-1 flex-shrink-0" weight="thin" />
                <div className="flex-1 min-w-0">
                  <div className="overline text-white/75 mb-1">Free · No obligation</div>
                  <div className="font-serif text-xl text-white">Book a property valuation</div>
                  <div className="text-sm text-white/75 mt-1">RERA-licensed advisors · 24-hour turnaround</div>
                </div>
                <ArrowRight size={18} className="text-white/70 group-hover:text-white group-hover:translate-x-1 transition-all mt-2 flex-shrink-0" />
              </Link>

              {/* 2 — Latest journal */}
              <Link to={latestJournal ? `/journal/${latestJournal.slug}` : "/journal"} data-testid="hero-action-journal"
                    className="group flex items-start gap-4 py-6 lg:py-7 px-6 lg:px-8 hover:bg-white/10 transition-colors">
                <Article size={26} className="text-white mt-1 flex-shrink-0" weight="thin" />
                <div className="flex-1 min-w-0">
                  <div className="overline text-white/75 mb-1">
                    Latest insight
                    {latestJournal?.created_at && (
                      <span className="ml-2 text-white/60" data-testid="hero-journal-date">
                        · {new Date(latestJournal.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                      </span>
                    )}
                  </div>
                  <div className="font-serif text-xl text-white line-clamp-2">
                    {latestJournal?.title || "Weekly Dubai market intelligence"}
                  </div>
                  <div className="text-sm text-white/75 mt-1">From the Point Penta research desk</div>
                </div>
                <ArrowRight size={18} className="text-white/70 group-hover:text-white group-hover:translate-x-1 transition-all mt-2 flex-shrink-0" />
              </Link>

              {/* 3 — Direct WhatsApp */}
              <a href="https://wa.me/971557396664?text=Hi%20Point%20Penta%2C%20I%27d%20like%20to%20speak%20to%20an%20advisor" target="_blank" rel="noreferrer"
                 data-testid="hero-action-whatsapp"
                 className="group flex items-start gap-4 py-6 lg:py-7 px-6 lg:px-8 hover:bg-white/10 transition-colors">
                <WhatsappLogo size={26} className="text-white mt-1 flex-shrink-0" weight="thin" />
                <div className="flex-1 min-w-0">
                  <div className="overline text-white/75 mb-1">Mon–Sat · 9:00–19:00 GST</div>
                  <div className="font-serif text-xl text-white">Speak to a partner</div>
                  <div className="text-sm text-white/75 mt-1">Direct WhatsApp · +971 55 739 6664</div>
                </div>
                <ArrowRight size={18} className="text-white/70 group-hover:text-white group-hover:translate-x-1 transition-all mt-2 flex-shrink-0" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="py-24 lg:py-32 border-t border-[#e5e7eb] bg-[#001588] text-white overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="md:col-span-5 md:sticky md:top-24">
              <div className="overline text-[#ffffff] mb-6">Manifesto · 01</div>
              <h2 className="font-serif text-4xl lg:text-6xl leading-[1.05] mb-10">
                We do not sell property.<br />
                <span>We advise on where to live.</span>
              </h2>
              <p className="text-[#f8faff] text-lg leading-relaxed max-w-lg">
                Point Penta was built on the belief that Dubai deserves a brokerage that treats every mandate as its own — with the same discipline a private bank brings to wealth.
              </p>
            </div>
            <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { n: "01", title: "No portals", copy: "You will not find our best mandates on Bayut or Property Finder. Curated inventory only." },
                { n: "02", title: "No listings farm", copy: "Six senior advisors. No junior brokers spraying leads. Every enquiry meets a partner." },
                { n: "03", title: "Aligned incentives", copy: "We charge for outcomes, not activity. Our fee structure rewards long-term client value." },
                { n: "04", title: "Institutional rigour", copy: "Investment memos, yield modelling, and due diligence — the standard used by family offices." },
              ].map((tile) => (
                <div key={tile.n} className="group relative border border-white/20 hover:border-[#ffffff] p-8 transition-all duration-500 hover:-translate-y-1">
                  <div className="font-serif text-6xl text-[#29338e] group-hover:text-[#ffffff] transition-colors mb-4">{tile.n}</div>
                  <h3 className="font-serif text-xl mb-3 text-white">{tile.title}</h3>
                  <p className="text-sm text-[#f8faff] leading-relaxed">{tile.copy}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED RESIDENCES */}
      <section className="py-24 lg:py-32 bg-[#ffffff]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="overline text-[#001588] mb-4">Currently on file</div>
              <h2 className="font-serif text-4xl lg:text-5xl leading-tight text-[#050d19]">Featured residences</h2>
            </div>
            <Link to="/properties" className="hidden md:flex items-center gap-2 text-sm text-neutral-600 hover:text-[#001588]" data-testid="home-view-all">
              View all mandates <ArrowRight size={16} />
            </Link>
          </div>
          {featured.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
              {featured.map((p) => <PropertyCard key={p.id} p={p} />)}
            </div>
          ) : (
            <div className="border border-dashed border-neutral-300 bg-white p-14 text-center text-neutral-500 text-sm">
              No mandates featured yet — mark listings as <em>Featured</em> in the admin dashboard to surface them here.
            </div>
          )}
        </div>
      </section>

      {/* FEATURED PROJECTS — NEW */}
      {featuredProjects.length > 0 && (
        <section className="py-24 lg:py-32 border-t border-[#e5e7eb] bg-white">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
            <div className="grid md:grid-cols-12 gap-8 mb-12 items-end">
              <div className="md:col-span-8">
                <div className="overline text-[#001588] mb-4">Off-plan spotlight</div>
                <h2 className="font-serif text-4xl lg:text-5xl leading-tight text-[#050d19]">
                  Featured off-plan.<br />
                  <span>Priority allocation still open.</span>
                </h2>
              </div>
              <div className="md:col-span-4 md:text-right">
                <Link to="/projects" className="inline-flex items-center gap-2 text-sm text-[#001588] border-b border-[#001588] pb-1 hover:gap-3 transition-all" data-testid="view-all-projects">
                  Explore all projects <ArrowRight size={16} />
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredProjects.map((p) => (
                <Link key={p.slug} to={`/projects/${p.slug}`} data-testid={`featured-project-${p.slug}`}
                      className="group relative overflow-hidden aspect-[4/5] block bg-neutral-900">
                  <img src={p.hero_image || HERO_IMG} alt={p.name}
                       className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                       loading="lazy"
                       onError={(e) => { e.currentTarget.src = HERO_IMG; }} />
                  {/* Stronger, more opaque scrim so pills + copy stay readable on any image */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/30"></div>
                  <div className="absolute top-4 left-4 flex gap-2">
                    <span className="text-[10px] uppercase tracking-[0.2em] bg-[#ffffff] text-black px-2.5 py-1 font-medium">Featured</span>
                    {p.status && <span className="text-[10px] uppercase tracking-[0.2em] bg-[#001588] text-white px-2.5 py-1 font-medium">{p.status}</span>}
                  </div>
                  <div className="absolute inset-0 p-6 flex flex-col justify-end text-white" style={{ textShadow: "0 1px 12px rgba(0,0,0,0.55)" }}>
                    <div className="overline text-white/90 mb-2">{p.developer_slug ? p.developer_slug.replace(/-/g, " ") : "Off-plan"}</div>
                    <h3 className="font-serif text-2xl lg:text-3xl leading-tight text-white">{p.name}</h3>
                    {p.community && <p className="text-sm text-white/90 mt-1">{p.community}</p>}
                    {p.starting_price && <p className="text-sm text-white mt-3 font-medium">from AED {Number(p.starting_price).toLocaleString()}</p>}
                    <div className="mt-4 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                      View project <ArrowUpRight size={14} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* COMMUNITIES */}
      <section className="py-24 lg:py-32 border-t border-[#e5e7eb] bg-gradient-to-b from-white to-[#ffffff]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-12 gap-8 mb-14 items-end">
            <div className="md:col-span-8">
              <div className="overline text-[#001588] mb-4">Where we operate</div>
              <h2 className="font-serif text-4xl lg:text-6xl leading-[1.05] text-[#050d19]">
                {areas.length === 5 ? "Five" : areas.length} neighbourhood{areas.length === 1 ? "" : "s"}.<br />
                <span>Every kind of Dubai.</span>
              </h2>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link to="/areas" className="inline-flex items-center gap-2 text-sm text-[#001588] border-b border-[#001588] pb-1 hover:gap-3 transition-all" data-testid="view-all-areas">
                Explore all communities <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-6 auto-rows-[16rem] lg:auto-rows-[20rem] gap-3 lg:gap-4">
            {areas.map((a, i) => {
              const spans = [
                "col-span-2 row-span-2",
                "col-span-2 lg:col-span-2 row-span-1",
                "col-span-2 lg:col-span-2 row-span-1",
                "col-span-2 lg:col-span-2 row-span-1",
                "col-span-2 lg:col-span-2 row-span-1",
              ];
              return (
                <Link key={a.slug} to={`/areas/${a.slug}`} data-testid={`area-tile-${a.slug}`}
                      className={`pp-hover-zoom relative overflow-hidden group bg-neutral-900 ${spans[i] || "col-span-2 row-span-1"}`}>
                  <img src={a.hero_image} alt={a.name}
                       className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                       loading="lazy" />
                  {/* Stronger scrim + full-image tint so the tagline stays legible on light images */}
                  <div className="absolute inset-0 bg-black/25"></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/75 to-black/20"></div>
                  <div className="absolute inset-0 p-6 lg:p-7 flex flex-col justify-end text-white" style={{ textShadow: "0 1px 12px rgba(0,0,0,0.55)" }}>
                    <div className="overline text-white/90 mb-2">{i === 0 ? "Featured community" : "Community"}</div>
                    <h3 className="font-serif text-2xl lg:text-3xl leading-tight text-white">{a.name}</h3>
                    <p className={`text-sm text-white/95 mt-2 max-w-md ${i === 0 ? "block" : "hidden lg:block"} line-clamp-2`}>{a.tagline}</p>
                    <div className="mt-4 h-px w-10 bg-white transition-all duration-500 group-hover:w-24"></div>
                    <div className="mt-3 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                      View area guide <ArrowRight size={14} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 lg:py-32 border-t border-[#e5e7eb]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="overline text-[#001588] mb-4">What we do</div>
          <h2 className="font-serif text-4xl lg:text-5xl leading-tight mb-14 text-[#050d19]">Five ways we work.</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { icon: Buildings, title: "Sales & Advisory", copy: "Discreet, off-market and on-market mandates across Dubai.", to: "/services" },
              { icon: Handshake, title: "Leasing", copy: "Residential and commercial leasing with tenant vetting.", to: "/rent" },
              { icon: MapTrifold, title: "Off-plan", copy: "Curated developer launches, priority allocation.", to: "/projects" },
              { icon: ChartLineUp, title: "Investment desk", copy: "Yield modelling, portfolio construction, Golden Visa strategy.", to: "/calculators" },
              { icon: Key, title: "Property management", copy: "Hand-off management for owners abroad — tenants, snagging, service charges.", to: "/services" },
            ].map(({ icon: Icon, title, copy, to }) => (
              <Link to={to} key={title} className="group block border border-[#e5e7eb] p-8 hover:border-[#001588] transition-colors bg-white">
                <Icon size={28} className="text-[#001588] mb-6" weight="thin" />
                <h3 className="font-serif text-2xl mb-3 group-hover:text-[#001588] text-[#050d19]">{title}</h3>
                <p className="text-sm text-neutral-600 leading-relaxed">{copy}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <TestimonialsStrip />

      {/* TOP-5 INVESTMENT COMMUNITIES — data-driven ranking. Auto-refreshes
          as new mandates land, gives repeat visitors a reason to check back
          and creates a strong AEO surface ("best areas to invest in Dubai"). */}
      {topInvest.length > 0 && (
        <section className="py-24 lg:py-32 border-t border-[#e5e7eb] bg-[#F5F7F6]" data-testid="home-top-invest">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
              <div>
                <div className="overline text-[#001588] mb-4">Live ranking · Q1 2026</div>
                <h2 className="font-serif text-4xl lg:text-5xl leading-tight text-[#050d19] max-w-3xl">
                  Top investment communities in Dubai right now.
                </h2>
                <p className="mt-4 text-sm text-neutral-600 max-w-xl leading-relaxed">
                  Ranked by our proprietary Point Penta investment score — blends live rental yield,
                  off-plan pipeline, active mandate inventory and Tier-1 developer presence. Refreshed daily.
                </p>
              </div>
              <Link to="/areas" className="text-sm tracking-[0.2em] uppercase text-[#001588] hover:underline flex items-center gap-2">
                Explore every community <ArrowRight size={16} weight="thin" />
              </Link>
            </div>
            <ol className="border-t border-neutral-300">
              {topInvest.map((c, i) => (
                <li key={c.slug}>
                  <Link
                    to={`/areas/${c.slug}`}
                    className="grid grid-cols-12 gap-4 items-center py-6 border-b border-neutral-300 hover:bg-white transition-colors group"
                    data-testid={`top-invest-${i}`}
                  >
                    <div className="col-span-1 font-serif text-3xl text-[#001588]">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="col-span-11 md:col-span-6">
                      <div className="font-serif text-xl sm:text-2xl group-hover:text-[#001588] transition-colors">{c.name}</div>
                      <div className="text-xs text-neutral-500 mt-1 uppercase tracking-[0.15em]">{c.emirate || "Dubai"}</div>
                    </div>
                    <div className="col-span-4 md:col-span-2 text-sm">
                      <div className="overline text-neutral-500 mb-1">Yield</div>
                      <div className="font-medium text-[#050d19]">{c.rental_yield ? `${c.rental_yield}%` : "—"}</div>
                    </div>
                    <div className="col-span-4 md:col-span-2 text-sm">
                      <div className="overline text-neutral-500 mb-1">Score</div>
                      <div className="font-medium text-[#001588]">{c.investment_score ? `${c.investment_score}/10` : "—"}</div>
                    </div>
                    <div className="hidden md:flex col-span-1 justify-end">
                      <ArrowUpRight size={20} weight="thin" className="text-neutral-400 group-hover:text-[#001588] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </div>
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* FREQUENTLY ASKED — visible + schema-backed for AEO reach. */}
      <section className="py-24 lg:py-28 border-t border-[#e5e7eb]" data-testid="home-faq">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-12">
          <div className="overline text-[#001588] mb-4">Frequently asked</div>
          <h2 className="font-serif text-4xl lg:text-5xl leading-tight text-[#050d19] mb-12">
            Dubai real estate — 2026 answers.
          </h2>
          <div className="divide-y divide-neutral-300 border-t border-neutral-300">
            {HOME_FAQS.map((f, i) => (
              <details key={i} className="py-6 group" data-testid={`home-faq-${i}`}>
                <summary className="cursor-pointer flex justify-between items-start gap-4 list-none">
                  <h3 className="text-base sm:text-lg font-medium text-[#050d19] group-open:text-[#001588]">{f.q}</h3>
                  <span className="text-[#001588] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm sm:text-base text-neutral-700 leading-relaxed max-w-3xl">{f.a}</p>
              </details>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/faq" className="text-sm tracking-[0.2em] uppercase text-[#001588] hover:underline">
              See the full FAQ →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32 border-t border-[#e5e7eb]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <div className="overline text-[#001588] mb-4">Enquire</div>
            <h2 className="font-serif text-4xl lg:text-6xl leading-tight text-[#050d19]">
              Selling, buying, or simply<br />
              <span>curious about a number?</span>
            </h2>
          </div>
          <div className="md:col-span-5 flex flex-col gap-4">
            <Link to="/valuation" className="px-6 py-4 bg-[#001588] text-white text-center tracking-wide hover:bg-black transition-colors" data-testid="cta-valuation">
              Free property valuation
            </Link>
            <Link to="/contact" className="px-6 py-4 border border-[#001588] text-[#001588] text-center tracking-wide hover:bg-[#001588] hover:text-white transition-colors" data-testid="cta-speak-advisor">
              Speak to an advisor
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
